import React, { useEffect, useState, useCallback, useContext} from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import { TextField, Box, Button } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../Firebase/firebase';
import { PostsContext } from '../../contexts/PostsContext';
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';

const AccordionExpand = ({
  postId,
  onCommentsCountChange = () => {}, // Add default empty function
  showSummary,
}) => {
  const { currentUser } = useAuth();
  const [userComment, setUserComment] = useState('');
  const [comments, setComments] = useState([]);
  const memoizedOnCommentsCountChange = useCallback(onCommentsCountChange, []);
  const { defaultAvatarImage } = useContext(PostsContext) || {};


  // console.log("inside AccordianExpand my username is:", currentUser.username)
  // Fetch comments for the specific post
  useEffect(() => {
    const q = query(
      collection(db, 'comments'),
      where('postId', '==', postId),
      orderBy('timestamp', 'asc') //sort comments by ascending order, latest comment at bottom
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedComments = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      // console.log('fetched comments are:', fetchedComments);
      setComments(fetchedComments);
      // Only call this if the function exists
      if (typeof onCommentsCountChange === 'function') {
        onCommentsCountChange(fetchedComments.length);
      }
    });
    return () => unsubscribe();
  }, [postId]);

  // Helper function to calculate the time difference
  const timeAgo = (timestamp) => {
    const now = new Date();
    const secondsAgo = Math.floor((now - timestamp) / 1000); // Difference in seconds
    const minutes = Math.floor(secondsAgo / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30); // Approximation
    const years = Math.floor(days / 365); // Approximation

    if (minutes < 60) {
      return `${minutes} min ago`;
    } else if (hours < 24) {
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (days < 7) {
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else if (weeks < 4) {
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else if (months < 12) {
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
      return `${years} year${years > 1 ? 's' : ''} ago`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (userComment.trim() === '') return;
    const gmailEmail = currentUser.providerData.find(
      (provider) => provider.providerId === 'google.com'
    )?.email;

    // Create a username from the text before the "@" sign in the Gmail email
    const username = gmailEmail
      ? gmailEmail.split('@')[0]
      : currentUser.email.split('@')[0];

    await addDoc(collection(db, 'comments'), {
      postId,
      userId: currentUser.uid,
      displayName: currentUser.displayName,
      email: currentUser.email,
      gmailEmail: gmailEmail,
      username: currentUser.username,
      userPhotoURL: currentUser.profileImage || currentUser.photo,
      comment: userComment,
      timestamp: serverTimestamp(),
    });
    setUserComment(''); // Clear the text field after submission
  };

  return (
    <Box sx={{ mt: 0.5, mb: 0 }}>
      <Accordion
        sx={{
          boxShadow: 'none',
          border: 'none',
        }}
      >
        {showSummary && (
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls='panel1-content'
            id='panel1-header'
            sx={{
              minHeight: {
                xs: '30px',
                sm: '30px',
                md: '30px',
                lg: '30px',
                xl: '30px',
              },
              height: {
                xs: '30px',
                sm: '30px',
                md: '30px',
                lg: '30px',
                xl: '30px',
              },
              '&.Mui-expanded': {
                minHeight: {
                  xs: '25px',
                  sm: '25px',
                  md: '30px',
                  lg: '45px',
                  xl: '45px',
                },
              },
              padding: '4px 8px',
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 11, sm: 11, md: 12, lg: 15, xl: 15 },
                fontWeight: 'bold',
                fontFamily: 'Montserrat',
              }}
            >
              Comments ({comments.length}):
            </Typography>
          </AccordionSummary>
        )}

        <AccordionDetails sx={{ pb: 0 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 3,
              overflowY: 'auto',
              maxHeight: '100px',
              '&:hover': { boxShadow: '0px 1px 2px inset grey' },
            }}
          >
            {comments.length > 0 ? (
              comments.map((comment, index) => {
                const commentTimestamp = comment.timestamp?.toDate(); // Convert Firestore timestamp to JS Date
                const timeAgoText = timeAgo(commentTimestamp); // Call the helper function to get time difference

                return (
                  <Box
                    key={comment.id}
                    sx={{
                      mb: 1,
                      display: 'flex',
                      flexDirection: {
                        xs: 'column',
                        sm: 'column',
                        md: 'column',
                        lg: 'column',
                        xl: 'column',
                      },
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                    }}
                  >
                    {/* {user.photoURL &&( */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        sx={{
                          p: 0,
                        }}
                      >
                        <Avatar
                          alt={comment.displayName || 'User'}
                          src={comment.userPhotoURL || defaultAvatarImage}
                          sx={{
                            width: { xs: 14, sm: 14, md: 14, lg: 18, xl: 18 },
                            height: { xs: 14, sm: 14, md: 14, lg: 18, xl: 18 },
                            mr: { xs: 0.3, sm: 0.3, md: 0.3, lg: 0.5, xl: 0.5 },
                            ml: { xs: 0, sm: 0, md: 0.25, lg: 0.5, xl: 0.5 },
                            fontSize: {
                              xs: 12,
                              sm: 12,
                              md: 13,
                              lg: 15,
                              xl: 15,
                            },
                            bgcolor: comment.userPhotoURL
                              ? comment.userPhotoURL
                              : 'indianred',
                          }}
                        >
                          {!comment.userPhotoURL &&
                            (comment.username?.[0] || 'U')}
                        </Avatar>
                      </IconButton>
                      <Typography
                        sx={{
                          fontSize: { xs: 8, sm: 8, md: 9, lg: 11, xl: 11 },
                          fontWeight: 'bold',
                          fontFamily: 'Montserrat',
                        }}
                      >
                        {comment.username}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: { xs: 8, sm: 8, md: 9, lg: 11, xl: 11 },
                          ml: { xs: 1, sm: 1, md: 1, lg: 0.5, xl: 0.5 },
                          color: 'gray',
                          fontStyle: 'italic',
                          fontFamily: 'Montserrat',
                        }}
                      >
                        ({timeAgoText})
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        fontSize: { xs: 8, sm: 8, md: 9, lg: 11, xl: 11 },
                        ml: { xs: 0, sm: 0, md: 0, lg: 3.5, xl: 3.5 },
                        fontFamily: 'Montserrat',
                      }}
                    >
                      {comment.comment}
                    </Typography>
                  </Box>
                );
              })
            ) : (
              <Typography
                sx={{
                  mb: 0,
                  fontSize: { xs: 9, sm: 10, md: 11, lg: 14, xl: 14 },
                  fontFamily: 'Montserrat',
                }}
              >
                No comments yet.
              </Typography>
            )}
          </Box>
          {/* Comment input form */}
          <Box
            component='form'
            onSubmit={handleSubmit}
            sx={{
              mt: 0.5,
              mb: 2,
              ml: { xs: 0, sm: 0, md: 1, lg: 1, xl: 1 },
              mr: { xs: 0, sm: 0, md: 1, lg: 1, xl: 1 },
              p: 1,
              borderRadius: 2,
            }}
          >
            <TextField
              fullWidth
              label='Leave Comment'
              variant='outlined'
              multiline
              rows={1}
              value={userComment}
              onChange={(e) => setUserComment(e.target.value)}
              required
              InputProps={{
                sx: {
                  fontFamily: 'Montserrat',
                  fontSize: {
                    xs: '12px',
                    sm: '12px',
                    md: '12px',
                    lg: '14px',
                    xl: '14px',
                  }, // Adjust the label font size
                },
              }}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'end',
                ml: 'auto',
                width: { xs: '10%', lg: '30%', xl: '30%' },
              }}
            >
              <Button
                variant='contained'
                color='primary'
                type='submit'
                sx={{
                  mt: 0.8,
                  fontSize: { xs: 8, sm: 8, md: 8, lg: 10, xl: 10 },
                  backgroundColor: 'darkblue',
                  borderRadius: '16px',
                }}
                disabled={!currentUser} // Disable if the user is not logged in
              >
                Submit
              </Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default AccordionExpand;
